import cx from 'clsx';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import React from 'react';
import { Tooltip } from 'react-tooltip';

import SkeletonBlock from '@components/common/Skeleton/SkeletonBlock';

import useTranslation from '@hooks/useTranslation';

import { selectAppPersistDarkMode } from '@store/useAppStorePersist';

import style from './style.module.scss';

interface ExtendedChart extends Highcharts.Chart {
  centerText: Highcharts.SVGElement;
}

interface IProps {
  title: string;
  isLoading: boolean;
  criticalAiCount: number | undefined;
  highAiCount: number | undefined;
  standardAiCount: number | undefined;
}

const AssetHealthOpenAi: React.FC<IProps> = ({
  title,
  isLoading,
  criticalAiCount = 0,
  highAiCount = 0,
  standardAiCount = 0,
}) => {
  const { t } = useTranslation();

  const darkMode = selectAppPersistDarkMode();

  const total = criticalAiCount + highAiCount + standardAiCount;

  const options: Highcharts.Options = {
    chart: {
      type: 'pie',
      backgroundColor: 'transparent',
      height: 48,
      width: 48,
      margin: 0,
      events: {
        render: function () {
          const chart = this as ExtendedChart;
          const series = chart.series[0];
          const textX = chart.plotLeft + series.center[0];

          if (chart.centerText) chart.centerText.destroy();

          chart.centerText = chart.renderer
            .text(`${total}`, textX, 28)
            .css({
              fontSize: '12px',
              fontWeight: 'bold',
              color: darkMode ? '#E0E2E6' : '#4A4A4A', // --legend-for-header-text
            })
            .add();

          chart.centerText.attr({
            x: textX - chart.centerText.getBBox().width / 2,
          });
        },
      },
    },
    title: {
      text: '',
    },
    subtitle: {
      text: '',
    },
    credits: {
      enabled: false,
    },
    tooltip: {
      enabled: false,
    },
    legend: {
      enabled: false,
    },
    plotOptions: {
      series: {
        allowPointSelect: false,
        cursor: 'pointer',
        dataLabels: [
          {
            enabled: false,
          },
          {
            enabled: false,
          },
        ],
      },
    },
    series: [
      {
        type: 'pie',
        slicedOffset: 0,
        borderRadius: 0,
        innerSize: '65%',
        data: [
          {
            y: criticalAiCount,
            color: darkMode ? '#F34B4B' : '#F24F52', // --labels-outline-stroke-text-critical
            borderColor: 'unset',
            sliced: true,
          },
          {
            y: highAiCount,
            color: darkMode ? '#F9845A' : '#FF7300', // --labels-outline-stroke-text-high
            borderColor: 'unset',
            sliced: true,
          },
          {
            y: standardAiCount,
            color: darkMode ? '#F0D980' : '#F8C71C', // --labels-outline-stroke-text-standard
            borderColor: 'unset',
            sliced: true,
          },
        ],
      },
    ],
  };

  return (
    <div className={style.infoBlock}>
      <div className={style.infoLeftBlock}>
        <span className={style.infoTitle}>{title}</span>
        <div className={style.openAiBlock}>
          <div className={style.openAi}>
            <div
              className={cx(style.openAiUrgency, style.critical)}
              data-tooltip-id="open-ai-description"
              data-tooltip-content={t('Critical action items')}
            />
            {isLoading ? <SkeletonBlock className={style.skeletonText} /> : criticalAiCount}
          </div>
          <div className={style.openAi}>
            <div
              className={cx(style.openAiUrgency, style.high)}
              data-tooltip-id="open-ai-description"
              data-tooltip-content={t('High action items')}
            />
            {isLoading ? <SkeletonBlock className={style.skeletonText} /> : highAiCount}
          </div>
          <div className={style.openAi}>
            <div
              className={cx(style.openAiUrgency, style.standard)}
              data-tooltip-id="open-ai-description"
              data-tooltip-content={t('Standard action items')}
            />
            {isLoading ? <SkeletonBlock className={style.skeletonText} /> : standardAiCount}
          </div>
          <Tooltip id="open-ai-description" place="top-start" />
        </div>
      </div>
      <div className={style.openAiChart}>
        {isLoading ? (
          <SkeletonBlock className={style.skeletonChart} />
        ) : (
          <HighchartsReact highcharts={Highcharts} options={options} />
        )}
      </div>
    </div>
  );
};

export default AssetHealthOpenAi;
